import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { AlertCircle, Paperclip, ArrowUp, Square, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChatColumn } from './ChatColumn';
import type { ChatInputState } from '@/components/ChatInput/useChatInput';
import type { AttachedFile } from '@/components/ChatInput/types';
import { StartupStatusBanner } from '@/components/ChatInput/StartupStatusBanner';
import { usePasteImageAttachment } from '@/components/ChatInput/usePasteImageAttachment';
import { AttachedFileThumbnail } from '@/components/ChatInput/AttachedFileThumbnail';
import { useTextareaAutoResize } from '@/hooks/useTextareaAutoResize';
import {
  setDevComponentPreview,
  useDevComponentPreview,
} from '@/lib/devComponentPreview';
import {
  PAPER_CHROME_RADIUS_CLASS,
  PAPER_ICON_COMPACT_CLASS,
  PAPER_ICON_CONTROL_COMPACT_CLASS,
  PAPER_ICON_CONTROL_MICRO_CLASS,
  PAPER_ICON_CONTROL_MINI_CLASS,
  PAPER_ICON_CONTROL_TOUCH_CLASS,
  PAPER_ICON_MICRO_CLASS,
  PAPER_ICON_SMALL_SIZE,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
  PAPER_MOBILE_CONTROL_RADIUS_CLASS,
} from './paperShapes';

export interface PaperChatInputProps extends ChatInputState {
  isRunning: boolean;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export const PaperAttachedFileChip = ({
  file,
  onRemove,
}: {
  file: ChatInputState['attachedFiles'][number];
  onRemove: (id: string) => void;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-[6px] h-[26px] px-[10px] rounded-full',
      'text-[10px] text-[var(--fg-3)]',
      'bg-[var(--surface-2)] border border-solid border-[var(--hair)]',
      file.status === 'error' &&
        'text-red-400 border-red-500/30 bg-red-500/[0.08]',
    )}
    title={file.status === 'error' ? file.error ?? 'Upload failed' : undefined}
  >
    {file.status === 'uploading' ? (
      <Loader2
        className={cn(PAPER_ICON_MICRO_CLASS, 'animate-spin')}
        strokeWidth={PAPER_ICON_STROKE_WIDTH}
      />
    ) : file.status === 'error' ? (
      <AlertCircle
        className={cn(PAPER_ICON_TINY_CLASS, 'shrink-0')}
        strokeWidth={PAPER_ICON_STROKE_WIDTH}
      />
    ) : (
      <AttachedFileThumbnail file={file} size={PAPER_ICON_SMALL_SIZE} className="text-[var(--fg-3)]" />
    )}
    {file.status === 'error' ? (
      <span className="flex min-w-0 flex-col leading-[1.1]">
        <span className="truncate max-w-[160px]">{file.file.name}</span>
        <span className="truncate max-w-[160px] text-[9px] tracking-normal text-red-300/80">
          {file.errorDetail ?? file.error ?? 'Upload failed'}
        </span>
      </span>
    ) : (
      <span className="truncate max-w-[160px]">{file.file.name}</span>
    )}
    {file.status !== 'uploading' && (
      <button
        type="button"
        onClick={() => onRemove(file.id)}
        className={cn(
          PAPER_ICON_CONTROL_MICRO_CLASS,
          'text-[var(--fg-4)]',
        )}
        aria-label="Remove file"
      >
        <X className={PAPER_ICON_MICRO_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
      </button>
    )}
  </span>
);

export interface PaperChatSubmitButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  type?: 'button' | 'submit';
  isSending?: boolean;
}

export const PaperChatSubmitButton = forwardRef<HTMLButtonElement, PaperChatSubmitButtonProps>(
  ({ type = 'submit', isSending = false, className, disabled, title = 'Send', ...buttonProps }, ref) => (
    <button
      {...buttonProps}
      ref={ref}
      type={type}
      disabled={disabled}
      title={title}
      className={cn(
        PAPER_ICON_CONTROL_COMPACT_CLASS,
        'bg-[var(--fg)] text-[var(--fg-inverse)] border-0',
        'transition-colors duration-150',
        'hover:bg-[var(--ink-3)]',
        'disabled:opacity-30 disabled:cursor-not-allowed',
        !disabled && 'cursor-pointer',
        PAPER_ICON_CONTROL_TOUCH_CLASS,
        PAPER_MOBILE_CONTROL_RADIUS_CLASS,
        className,
      )}
    >
      {isSending ? (
        <Loader2
          className={cn(PAPER_ICON_TINY_CLASS, 'animate-spin')}
          strokeWidth={PAPER_ICON_STROKE_WIDTH}
        />
      ) : (
        <ArrowUp className={PAPER_ICON_COMPACT_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
      )}
    </button>
  ),
);
PaperChatSubmitButton.displayName = 'PaperChatSubmitButton';

export const PaperChatInput = (props: PaperChatInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const devPreviews = useDevComponentPreview();
  const { ref: taRef, handleChange: handleTextareaChange } = useTextareaAutoResize(
    null,
    props.message,
    (e) => props.onMessageChange(e.target.value),
    200,
  );

  const handleFileClick = () => {
    if (props.isBlocked || props.isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      void props.onAttachFiles(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (props.isRunning) return;
      const form = e.currentTarget.form;
      if (form) form.requestSubmit();
    }
  };

  const handlePaste = usePasteImageAttachment({
    vmAvailable: props.vmAvailable,
    onAttachFiles: props.onAttachFiles,
  });

  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const dropEnabled = props.vmAvailable && !props.isBlocked;

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!dropEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current++;
      if (dragCounterRef.current === 1) setIsDragOver(true);
    },
    [dropEnabled],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!dropEnabled) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [dropEnabled],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!dropEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) setIsDragOver(false);
    },
    [dropEnabled],
  );

  useEffect(() => {
    if (!dropEnabled) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, [dropEnabled]);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!dropEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        void props.onAttachFiles(e.dataTransfer.files);
      }
    },
    [dropEnabled, props.onAttachFiles],
  );

  const previewAttachedFiles = useMemo<AttachedFile[]>(() => {
    const files: AttachedFile[] = [];
    if (devPreviews.attachmentUploadError) {
      files.push({
        id: 'dev-preview-upload-error',
        file: new File(['preview'], 'screenshot.png', { type: 'image/png' }),
        status: 'error',
        progress: 0,
        error: 'Invalid upload response',
      });
    }
    if (devPreviews.attachmentValidationError) {
      files.push({
        id: 'dev-preview-validation-error',
        file: new File(['preview'], 'archive.zip', { type: 'application/zip' }),
        status: 'error',
        progress: 0,
        error: 'Invalid format',
        errorDetail: 'Invalid format',
      });
    }
    return files;
  }, [devPreviews.attachmentUploadError, devPreviews.attachmentValidationError]);

  const visibleAttachedFiles = [...props.attachedFiles, ...previewAttachedFiles];
  const hasVisibleAttachmentErrors =
    props.hasAttachmentErrors || previewAttachedFiles.length > 0;

  const canSend =
    props.message.trim().length > 0 &&
    !props.isRunning &&
    !props.isSending &&
    !props.isBlocked &&
    !props.isUploading &&
    !hasVisibleAttachmentErrors;

  const handleSubmit = (e: FormEvent) => {
    if (previewAttachedFiles.length > 0) {
      e.preventDefault();
      return;
    }
    props.onSubmit(e);
  };

  const handleRemoveFile = (id: string) => {
    if (id === 'dev-preview-upload-error') {
      setDevComponentPreview('attachmentUploadError', false);
      return;
    }
    if (id === 'dev-preview-validation-error') {
      setDevComponentPreview('attachmentValidationError', false);
      return;
    }
    props.onRemoveFile(id);
  };

  return (
    <ChatColumn padded className={cn('shrink-0 pb-[20px]', props.className)}>
        {props.vmAvailable && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFilesSelected}
          />
        )}

        {visibleAttachedFiles.length > 0 && (
          <div className="mb-[8px] flex flex-wrap gap-[6px]">
            {visibleAttachedFiles.map((f) => (
              <PaperAttachedFileChip
                key={f.id}
                file={f}
                onRemove={handleRemoveFile}
              />
            ))}
          </div>
        )}

        {props.startupStatus && (
          <div className="mb-[8px]">
            <StartupStatusBanner status={props.startupStatus} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'relative bg-[var(--surface)] border border-solid border-[var(--hair)]',
              PAPER_CHROME_RADIUS_CLASS,
              'py-[12px] px-[14px]',
              // Mobile: single-row layout (textarea inline with paperclip
              // + send on the right). Keep the desktop 12px radius for
              // visual consistency with chips/cards; just retune padding.
              'mobile:py-[6px] mobile:pl-[14px] mobile:pr-[6px]',
              'transition-[border-color,box-shadow] duration-200',
              'focus-within:border-[var(--periwinkle)] focus-within:shadow-[0_0_0_3px_var(--periwinkle-wash)]',
              isDragOver &&
                'border-[var(--periwinkle)] shadow-[0_0_0_3px_var(--periwinkle-wash)]',
            )}
          >
            {isDragOver && (
              <div
                className={cn(
                  'pointer-events-none absolute inset-0 flex items-center justify-center bg-[var(--periwinkle-wash)]',
                  PAPER_CHROME_RADIUS_CLASS,
                )}
              >
                <span
                  className={cn(
                    'bg-[var(--surface)] border border-solid border-[var(--periwinkle)] px-[10px] py-[4px] text-[11px] text-[var(--fg-2)]',
                    PAPER_CHROME_RADIUS_CLASS,
                  )}
                  style={{ fontFamily: 'var(--font-sans-paper)' }}
                >
                  Drop files to attach
                </span>
              </div>
            )}
            <div className="flex flex-col mobile:flex-row mobile:items-center mobile:gap-[6px]">
              <textarea
                ref={taRef}
                value={props.message}
                onChange={handleTextareaChange}
                onKeyDown={handleKey}
                onPaste={handlePaste}
                placeholder={props.placeholder ?? 'Message…'}
                rows={1}
                autoFocus={props.autoFocus}
                disabled={props.isBlocked}
                className={cn(
                  'w-full resize-none bg-transparent outline-none border-0',
                  'text-[14px] leading-[1.5] text-[var(--fg)] break-words',
                  'placeholder:text-[var(--fg-4)] min-h-[22px] max-h-[240px]',
                  'disabled:cursor-not-allowed',
                  'mobile:flex-1 mobile:min-w-0 mobile:py-[6px]',
                )}
                style={{ fontFamily: 'var(--font-sans-paper)' }}
              />
              <div
                className={cn(
                  'flex items-center justify-between mt-[12px]',
                  'mobile:mt-0 mobile:shrink-0 mobile:justify-end mobile:gap-[2px]',
                )}
              >
                {props.vmAvailable ? (
                  <button
                    type="button"
                    onClick={handleFileClick}
                    disabled={props.isBlocked || props.isUploading}
                    title="Attach file"
                    className={cn(
                      PAPER_ICON_CONTROL_MINI_CLASS,
                      PAPER_ICON_CONTROL_TOUCH_CLASS,
                      PAPER_MOBILE_CONTROL_RADIUS_CLASS,
                    )}
                  >
                    <Paperclip className={PAPER_ICON_COMPACT_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
                  </button>
                ) : (
                  // Desktop spacer to keep the send button right-aligned via
                  // justify-between. On mobile the action row uses
                  // justify-end so the spacer is unnecessary; hide it.
                  <span className="mobile:hidden" />
                )}

                {props.isRunning ? (
                  <button
                    type="button"
                    onClick={props.onStop}
                    title="Stop"
                    className={cn(
                      PAPER_ICON_CONTROL_COMPACT_CLASS,
                      'bg-[var(--fg)] text-[var(--fg-inverse)] border-0 cursor-pointer',
                      'transition-colors duration-150 hover:bg-[var(--ink-3)]',
                      PAPER_ICON_CONTROL_TOUCH_CLASS,
                      PAPER_MOBILE_CONTROL_RADIUS_CLASS,
                    )}
                  >
                    <Square
                      className={PAPER_ICON_TINY_CLASS}
                      fill="currentColor"
                      strokeWidth={PAPER_ICON_STROKE_WIDTH}
                    />
                  </button>
                ) : (
                  <PaperChatSubmitButton
                    type="submit"
                    disabled={!canSend}
                    isSending={props.isSending}
                  />
                )}
              </div>
            </div>
          </div>
        </form>
    </ChatColumn>
  );
};
